import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BookOpen, Search, Tag, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const ResourceHub = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await api.get('/resources/list');
      setResources(response.data.filter(r => !r.premium));
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(resources.map(r => r.category))];

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          resource.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
              Knowledge Hub
            </h1>
            <p className="text-xl text-slate-300">
              Free educational resources, guides, and tools for non-CDL carriers
            </p>
          </motion.div>
        </div>
      </section>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Search and Filter */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search free resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              data-testid="resource-hub-search-input"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${
                  selectedCategory === category
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`category-${category}`}
              >
                {category === 'all' ? 'All Resources' : category}
              </button>
            ))}
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredResources.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-white border border-slate-200 rounded-md">
              <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No resources found matching your criteria.</p>
            </div>
          ) : (
            filteredResources.map((resource, index) => (
              <motion.div
                key={resource.id || resource.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-slate-200 rounded-md p-6 hover:border-blue-300 hover:shadow-md transition-all card-hover flex flex-col"
                data-testid={`resource-card-${resource.id || index}`}
              >
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mb-3 w-fit">
                  {resource.category}
                </span>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                  {resource.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4 flex-1">{resource.content}</p>
                <div className="flex flex-wrap gap-1 mb-4">
                  {resource.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs"
                    >
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                </div>
                <button
                  className="bg-slate-900 text-white px-4 py-2 rounded-md font-semibold hover:bg-slate-800 transition-all text-sm"
                  data-testid={`read-resource-${resource.id || index}`}
                >
                  Read More
                </button>
              </motion.div>
            ))
          )}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md p-8 shadow-lg"
        >
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl font-bold mb-4">
              Want Access to Premium Resources?
            </h2>
            <p className="text-blue-100 mb-6">
              Join StraightConnect to unlock premium guides, grant databases, and advanced training materials.
            </p>
            <Link
              to="/join"
              className="inline-block bg-white text-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-all btn-hover-lift"
              data-testid="join-cta-btn"
            >
              Join the Network
              <ArrowRight className="inline ml-2 h-5 w-5" />
            </Link>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default ResourceHub;